import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

/**
 * The app's one floating Back button: a solid pill pinned to the top-left, above the
 * page content. Used both where the sticky header collapses away on scroll and over
 * immersive photo heroes, so navigating into a detail screen keeps the same control.
 * Solid (not translucent) so it stays legible over bright photos and maps alike.
 */
export function FloatingBackButton({
  onClick,
  label = "Back",
}: {
  onClick: () => void;
  label?: string;
}): JSX.Element {
  const reduceMotion = useReducedMotion();

  return (
    // The wrapper owns the pinning and the spawn animation; keeping the press feedback
    // on a separate child means framer's inline transform can't clobber `active:scale-95`.
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 500, damping: 34 }}
      className="pointer-events-none fixed left-4 top-[max(0.75rem,calc(env(safe-area-inset-top)+0.25rem))] z-40"
    >
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        className="pointer-events-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary-700 text-white shadow-lg shadow-black/20 transition-transform duration-fast active:scale-95"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </motion.div>
  );
}
